import Ajv from 'ajv';
import type { Options } from 'ajv';
import addFormats from 'ajv-formats';
import ajvErrors from 'ajv-errors';
import apiSpec from '@/api/openapi.json';
import { applyGlobalPatches, globalPatches, applyPatch, schemaPatches } from './patches/patches';

type Schemas = typeof apiSpec.components.schemas;
export type SchemaName = keyof Schemas;

const ajvOptions: Options = {
  allErrors: true,
  inlineRefs: 1000,
  $data: true,
  discriminator: true,
  strictRequired: 'log',
  strictSchema: 'log',
};

export function useAjv(options: Options = ajvOptions) {
  const ajv = new Ajv(options);
  ajvErrors(ajv);

  addFormats(ajv);
  ajv.addFormat('tel', { type: 'string', validate: /.*?/ });
  ajv.addKeyword('x-enumNames');
  ajv.addFormat('time', /^(?:(?:([01]?\d|2[0-3]):)?([0-5]?\d):)?([0-5]?\d)$/);

  function getSchema<T extends SchemaName>(modelType: T) {
    const validateSchema = ajv.getSchema<Schemas[T]>(modelType);
    if (!validateSchema) {
      throw new Error(`schema not defined for "${modelType}"`);
    }
    return validateSchema;
  }

  /** Replace $ref within a schema by the Schema-ID, so that AJV can resolve it
   * #/components/schemas/DataSetDto --> DataSetDto
   * https://ajv.js.org/guide/combining-schemas.html#combining-schemas-with-ref
   * https://gist.github.com/tushariscoolster/567c1d22ca8d5498cbc0
   */
  function initialize() {
    applyGlobalPatches(apiSpec, globalPatches);

    const traverse = (obj: any) => {
      for (const k in obj) {
        if (obj[k] && typeof obj[k] === 'object') {
          traverse(obj[k]);
        } else if (k === '$ref') {
          const targetRef = obj[k].substring(21);
          obj[k] = targetRef;
        }
      }
    };

    for (const [key, schema] of Object.entries(apiSpec.components.schemas)) {
      traverse(schema);
      applyPatch(schemaPatches, apiSpec, key, schema);
      ajv.addSchema(schema, key);
    }
  }

  return { ajv, getSchema, initialize };
}
